import React from 'react';
import './StatusIndicator.css';

/**
 * StatusIndicator component for displaying status information
 * Based on ArbiWin design system
 *
 * @param {Object} props - StatusIndicator properties
 * @param {string} props.status - Status type: 'online', 'offline', 'pending', 'success', 'error', 'warning', 'active', 'inactive'
 * @param {string} props.label - Status label text
 * @param {string} props.size - Indicator size: 'small', 'medium', 'large'
 * @param {boolean} props.pulse - Whether to show pulse animation
 * @param {boolean} props.showLabel - Whether to show status label
 * @param {string} props.position - Label position: 'right', 'left', 'top', 'bottom'
 * @param {React.ReactNode} props.icon - Custom icon component
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} StatusIndicator component
 */
const StatusIndicator = function StatusIndicator({
  status = 'inactive',
  label,
  size = 'medium',
  pulse = false,
  showLabel = false,
  position = 'right',
  icon,
  className = '',
  testId
}) {

  const indicatorClasses = [
    'arbiwin-status-indicator',
    'arbiwin-status-indicator--' + status,
    'arbiwin-status-indicator--' + size,
    'arbiwin-status-indicator--position-' + position,
    pulse ? 'arbiwin-status-indicator--pulse' : '',
    showLabel ? 'arbiwin-status-indicator--with-label' : '',
    className
  ].filter(Boolean).join(' ');

  const getStatusLabel = function() {
    if (label) return label;

    const defaultLabels = {
      online: 'Онлайн',
      offline: 'Офлайн',
      pending: 'Изчакващ',
      success: 'Успешно',
      error: 'Грешка',
      warning: 'Внимание',
      active: 'Активен',
      inactive: 'Неактивен'
    };

    return defaultLabels[status] || status;
  };

  const getStatusIcon = function() {
    if (icon) return icon;

    const icons = {
      online: (
        <svg className="arbiwin-status-indicator__icon" viewBox="0 0 8 8" fill="currentColor">
          <circle cx="4" cy="4" r="3" />
        </svg>
      ),
      offline: (
        <svg className="arbiwin-status-indicator__icon" viewBox="0 0 8 8" fill="currentColor">
          <circle cx="4" cy="4" r="3" />
        </svg>
      ),
      pending: (
        <svg className="arbiwin-status-indicator__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
      ),
      success: (
        <svg className="arbiwin-status-indicator__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      error: (
        <svg className="arbiwin-status-indicator__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      warning: (
        <svg className="arbiwin-status-indicator__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      active: (
        <svg className="arbiwin-status-indicator__icon" viewBox="0 0 8 8" fill="currentColor">
          <circle cx="4" cy="4" r="3" />
        </svg>
      ),
      inactive: (
        <svg className="arbiwin-status-indicator__icon" viewBox="0 0 8 8" fill="currentColor">
          <circle cx="4" cy="4" r="3" />
        </svg>
      )
    };

    return icons[status] || icons.inactive;
  };

  return (
    <div
      className={indicatorClasses}
      title={getStatusLabel()}
      data-testid={testId || 'arbiwin-status-indicator'}
    >
      <div className="arbiwin-status-indicator__dot">
        {getStatusIcon()}
      </div>

      {showLabel && (
        <span className="arbiwin-status-indicator__label">
          {getStatusLabel()}
        </span>
      )}
    </div>
  );
};

/**
 * ConnectionStatus component - Pre-configured for connection status
 *
 * @param {Object} props - ConnectionStatus properties
 * @param {boolean} props.isConnected - Connection status
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ConnectionStatus component
 */
const ConnectionStatus = function ConnectionStatus({
  isConnected = false,
  className = ''
}) {
  return (
    <StatusIndicator
      status={isConnected ? 'online' : 'offline'}
      label={isConnected ? 'Свързан' : 'Прекъсната връзка'}
      size="small"
      pulse={isConnected}
      showLabel={true}
      className={'arbiwin-connection-status ' + className}
    />
  );
};

/**
 * SubscriptionStatus component - Pre-configured for subscription status
 *
 * @param {Object} props - SubscriptionStatus properties
 * @param {string} props.status - Subscription status: 'active', 'inactive', 'expired', 'pending'
 * @param {string} props.expiryDate - Subscription expiry date
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} SubscriptionStatus component
 */
const SubscriptionStatus = function SubscriptionStatus({
  status = 'inactive',
  expiryDate,
  className = ''
}) {

  const getStatusConfig = function() {
    const configs = {
      active: {
        status: 'success',
        label: 'Активен абонамент',
        pulse: true
      },
      inactive: {
        status: 'inactive',
        label: 'Неактивен абонамент',
        pulse: false
      },
      expired: {
        status: 'error',
        label: 'Изтекъл абонамент',
        pulse: false
      },
      pending: {
        status: 'pending',
        label: 'Изчакващ активация',
        pulse: true
      }
    };
    return configs[status] || configs.inactive;
  };

  const config = getStatusConfig();
  const label = expiryDate && status === 'active'
    ? `${config.label} (до ${expiryDate})`
    : config.label;

  return (
    <StatusIndicator
      status={config.status}
      label={label}
      size="medium"
      pulse={config.pulse}
      showLabel={true}
      className={'arbiwin-subscription-status ' + className}
    />
  );
};

/**
 * DataFreshness component - Pre-configured for data freshness status
 *
 * @param {Object} props - DataFreshness properties
 * @param {Date} props.lastUpdate - Last update timestamp
 * @param {number} props.staleThreshold - Threshold in minutes for stale data
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} DataFreshness component
 */
const DataFreshness = function DataFreshness({
  lastUpdate,
  staleThreshold = 10,
  className = ''
}) {

  const getStatus = function() {
    if (!lastUpdate) {
      return { status: 'inactive', label: 'Няма данни' };
    }

    const now = new Date();
    const updateTime = new Date(lastUpdate);
    const minutesSinceUpdate = Math.floor((now - updateTime) / (1000 * 60));

    if (minutesSinceUpdate <= 1) {
      return { status: 'online', label: 'Актуални данни', pulse: true };
    } else if (minutesSinceUpdate <= staleThreshold) {
      return { status: 'success', label: `Обновени преди ${minutesSinceUpdate} мин`, pulse: false };
    } else {
      return { status: 'warning', label: `Остарели данни (${minutesSinceUpdate} мин)`, pulse: false };
    }
  };

  const statusConfig = getStatus();

  return (
    <StatusIndicator
      status={statusConfig.status}
      label={statusConfig.label}
      size="small"
      pulse={statusConfig.pulse}
      showLabel={true}
      className={'arbiwin-data-freshness ' + className}
    />
  );
};

StatusIndicator.displayName = 'StatusIndicator';
ConnectionStatus.displayName = 'ConnectionStatus';
SubscriptionStatus.displayName = 'SubscriptionStatus';
DataFreshness.displayName = 'DataFreshness';

export default StatusIndicator;
export { ConnectionStatus, SubscriptionStatus, DataFreshness };
